import { TFunction } from 'i18next';

/**
 * Metric definition pairing the on-page label with its glossary description.
 * Mirrors the Email Pulse pattern (`EMAIL_PULSE_DEFINITIONS`): `getLabel`
 * references the SAME translation key the page renders (summary cards, table
 * columns, …), so the Report Fields drawer can never drift from the UI.
 *
 * IMPORTANT: Use literal translation keys in getLabel/getDescription so the
 * i18next scanner keeps them. Do NOT use variables or string concatenation.
 */
export type GuestJourneyMetricDefinition = {
  /** Returns the translated on-page label (same key the page renders). */
  getLabel: (t: TFunction) => string;
  /** Returns the translated glossary description for the drawer. */
  getDescription: (t: TFunction) => string;
};

/**
 * Central registry of Guest Journey metrics that appear both on a tab and in
 * the Report Fields drawer. Single source of truth for labels + descriptions.
 * Metrics that repeat across tabs (e.g. Members Who Ordered) get one entry per
 * tab because their descriptions differ (different denominators/windows).
 */
export const GUEST_JOURNEY_DEFINITIONS: Record<
  string,
  GuestJourneyMetricDefinition
> = {
  // Active — summary cards
  activeNewMembers: {
    getLabel: t => t('page.guestJourney.activeCustomers.summary.newMembers'),
    getDescription: t => t('page.guestJourney.definitions.activeNewMembers'),
  },
  activeConversionRate: {
    getLabel: t =>
      t('page.guestJourney.activeCustomers.summary.conversionRate'),
    getDescription: t =>
      t('page.guestJourney.definitions.activeConversionRate'),
  },
  activeTotalMembers: {
    getLabel: t => t('page.guestJourney.activeCustomers.summary.totalMembers'),
    getDescription: t => t('page.guestJourney.definitions.activeTotalMembers'),
  },
  activeMembersOrderedAllTime: {
    getLabel: t =>
      t('page.guestJourney.activeCustomers.summary.membersOrderedAllTime'),
    getDescription: t =>
      t('page.guestJourney.definitions.activeMembersOrderedAllTime'),
  },
  activeTotalSlidersSaved: {
    getLabel: t =>
      t('page.guestJourney.activeCustomers.summary.totalSlidersSaved'),
    getDescription: t =>
      t('page.guestJourney.definitions.activeTotalSlidersSaved'),
  },
  activeTotalChurnedSaved: {
    getLabel: t =>
      t('page.guestJourney.activeCustomers.summary.totalChurnedSaved'),
    getDescription: t =>
      t('page.guestJourney.definitions.activeTotalChurnedSaved'),
  },

  // Sliding — summary cards
  slidersMembersOrderedAllTime: {
    getLabel: t => t('page.guestJourney.sliders.summary.membersOrderedAllTime'),
    getDescription: t =>
      t('page.guestJourney.definitions.slidersMembersOrderedAllTime'),
  },
  slidersSliders: {
    getLabel: t => t('page.guestJourney.sliders.summary.sliders'),
    getDescription: t => t('page.guestJourney.definitions.slidersSliders'),
  },
  slidersSliderRate: {
    getLabel: t => t('page.guestJourney.sliders.summary.sliderRate'),
    getDescription: t => t('page.guestJourney.definitions.slidersSliderRate'),
  },
  slidersMedianDaysFromLastOrder: {
    getLabel: t =>
      t('page.guestJourney.sliders.summary.medianDaysFromLastOrder'),
    getDescription: t =>
      t('page.guestJourney.definitions.slidersMedianDaysFromLastOrder'),
  },
  slidersProjectedAnnualRevenue: {
    getLabel: t =>
      t('page.guestJourney.sliders.summary.projectedAnnualRevenue'),
    getDescription: t =>
      t('page.guestJourney.definitions.slidersProjectedAnnualRevenue'),
  },

  // Churn — summary cards
  churnedChurnThreshold: {
    getLabel: t => t('page.guestJourney.churned.summary.churnThreshold'),
    getDescription: t =>
      t('page.guestJourney.definitions.churnedChurnThreshold'),
  },
  churnedMembersOrderedAllTime: {
    getLabel: t => t('page.guestJourney.churned.summary.membersOrderedAllTime'),
    getDescription: t =>
      t('page.guestJourney.definitions.churnedMembersOrderedAllTime'),
  },
  churnedChurnedMembers: {
    getLabel: t => t('page.guestJourney.churned.summary.churnedMembers'),
    getDescription: t =>
      t('page.guestJourney.definitions.churnedChurnedMembers'),
  },
  churnedChurnRate: {
    getLabel: t => t('page.guestJourney.churned.summary.churnRate'),
    getDescription: t => t('page.guestJourney.definitions.churnedChurnRate'),
  },
  churnedMedianDaysFromLastOrder: {
    getLabel: t =>
      t('page.guestJourney.churned.summary.medianDaysFromLastOrder'),
    getDescription: t =>
      t('page.guestJourney.definitions.churnedMedianDaysFromLastOrder'),
  },
  churnedLifetimeRevenue: {
    getLabel: t => t('page.guestJourney.churned.summary.lifetimeRevenue'),
    getDescription: t =>
      t('page.guestJourney.definitions.churnedLifetimeRevenue'),
  },

  // Stage Movement — summary cards
  stageCurrentTotalMembers: {
    getLabel: t =>
      t('page.guestJourney.stageMovement.summary.currentTotalMembers'),
    getDescription: t =>
      t('page.guestJourney.definitions.stageCurrentTotalMembers'),
  },
  stageIncomingMembers: {
    getLabel: t => t('page.guestJourney.stageMovement.summary.incomingMembers'),
    getDescription: t =>
      t('page.guestJourney.definitions.stageIncomingMembers'),
  },
  stageOutgoingMembers: {
    getLabel: t => t('page.guestJourney.stageMovement.summary.outgoingMembers'),
    getDescription: t =>
      t('page.guestJourney.definitions.stageOutgoingMembers'),
  },
  stageFrequency: {
    getLabel: t => t('page.guestJourney.stageMovement.summary.frequency'),
    getDescription: t => t('page.guestJourney.definitions.stageFrequency'),
  },
  stageTotalSpend: {
    getLabel: t => t('page.guestJourney.stageMovement.summary.totalSpend'),
    getDescription: t => t('page.guestJourney.definitions.stageTotalSpend'),
  },

  // Offer Impact — summary cards (12, point-in-time)
  offerAvgCheck30d: {
    getLabel: t => t('page.guestJourney.offerImpact.summary.avgCheck30d'),
    getDescription: t => t('page.guestJourney.definitions.offerAvgCheck30d'),
  },
  offerAvgVisits30d: {
    getLabel: t => t('page.guestJourney.offerImpact.summary.avgVisits30d'),
    getDescription: t => t('page.guestJourney.definitions.offerAvgVisits30d'),
  },
  offerAvgCheck90d: {
    getLabel: t => t('page.guestJourney.offerImpact.summary.avgCheck90d'),
    getDescription: t => t('page.guestJourney.definitions.offerAvgCheck90d'),
  },
  offerAvgVisits90d: {
    getLabel: t => t('page.guestJourney.offerImpact.summary.avgVisits90d'),
    getDescription: t => t('page.guestJourney.definitions.offerAvgVisits90d'),
  },
  offerDiscountRatio: {
    getLabel: t => t('page.guestJourney.offerImpact.summary.discountRatio'),
    getDescription: t => t('page.guestJourney.definitions.offerDiscountRatio'),
  },
  offerDistributions: {
    getLabel: t => t('page.guestJourney.offerImpact.summary.distributions'),
    getDescription: t => t('page.guestJourney.definitions.offerDistributions'),
  },
  offerTotalRedemptions: {
    getLabel: t => t('page.guestJourney.offerImpact.summary.totalRedemptions'),
    getDescription: t =>
      t('page.guestJourney.definitions.offerTotalRedemptions'),
  },
  offerRedemptionRate: {
    getLabel: t => t('page.guestJourney.offerImpact.summary.redemptionRate'),
    getDescription: t => t('page.guestJourney.definitions.offerRedemptionRate'),
  },
  offerStageImpact: {
    getLabel: t => t('page.guestJourney.offerImpact.summary.stageImpact'),
    getDescription: t => t('page.guestJourney.definitions.offerStageImpact'),
  },
  offerTotalDiscount: {
    getLabel: t => t('page.guestJourney.offerImpact.summary.totalDiscount'),
    getDescription: t => t('page.guestJourney.definitions.offerTotalDiscount'),
  },
  offerTotalSpend: {
    getLabel: t => t('page.guestJourney.offerImpact.summary.totalSpend'),
    getDescription: t => t('page.guestJourney.definitions.offerTotalSpend'),
  },
  offerAvgCheckOnRedemption: {
    getLabel: t =>
      t('page.guestJourney.offerImpact.summary.avgCheckOnRedemption'),
    getDescription: t =>
      t('page.guestJourney.definitions.offerAvgCheckOnRedemption'),
  },

  // Movement tables (shared by Stage Movement + Offer Impact)
  movementStage: {
    getLabel: t => t('page.guestJourney.movement.table.columns.stage'),
    getDescription: t => t('page.guestJourney.definitions.movementStage'),
  },
  movementCustomersMoved: {
    getLabel: t => t('page.guestJourney.movement.table.columns.customersMoved'),
    getDescription: t =>
      t('page.guestJourney.definitions.movementCustomersMoved'),
  },
  movementVisits: {
    getLabel: t => t('page.guestJourney.movement.table.columns.visits'),
    getDescription: t => t('page.guestJourney.definitions.movementVisits'),
  },
  movementNetSales: {
    getLabel: t => t('page.guestJourney.movement.table.columns.netSales'),
    getDescription: t => t('page.guestJourney.definitions.movementNetSales'),
  },
  movementMovement: {
    getLabel: t => t('page.guestJourney.movement.table.columns.movement'),
    getDescription: t => t('page.guestJourney.definitions.movementMovement'),
  },

  // Details — per-stage table columns
  detailsCurrentTotalMembers: {
    getLabel: t =>
      t('page.guestJourney.details.table.columns.currentTotalMembers'),
    getDescription: t =>
      t('page.guestJourney.definitions.detailsCurrentTotalMembers'),
  },
  detailsTotalVisits: {
    getLabel: t => t('page.guestJourney.details.table.columns.totalVisits'),
    getDescription: t => t('page.guestJourney.definitions.detailsTotalVisits'),
  },
  detailsTotalSpend: {
    getLabel: t => t('page.guestJourney.details.table.columns.totalSpend'),
    getDescription: t => t('page.guestJourney.definitions.detailsTotalSpend'),
  },
  detailsAverageOrderSize: {
    getLabel: t =>
      t('page.guestJourney.details.table.columns.averageOrderSize'),
    getDescription: t =>
      t('page.guestJourney.definitions.detailsAverageOrderSize'),
  },
  detailsMedianDaysBetweenVisits: {
    getLabel: t =>
      t('page.guestJourney.details.table.columns.medianDaysBetweenVisits'),
    getDescription: t =>
      t('page.guestJourney.definitions.detailsMedianDaysBetweenVisits'),
  },

  // Retention — LTV cards
  retentionSpend30: {
    getLabel: t => t('page.guestJourney.retention.ltv.spend30'),
    getDescription: t => t('page.guestJourney.definitions.retentionSpend30'),
  },
  retentionSpend90: {
    getLabel: t => t('page.guestJourney.retention.ltv.spend90'),
    getDescription: t => t('page.guestJourney.definitions.retentionSpend90'),
  },
  retentionSpend180: {
    getLabel: t => t('page.guestJourney.retention.ltv.spend180'),
    getDescription: t => t('page.guestJourney.definitions.retentionSpend180'),
  },
  retentionSpend365: {
    getLabel: t => t('page.guestJourney.retention.ltv.spend365'),
    getDescription: t => t('page.guestJourney.definitions.retentionSpend365'),
  },

  // Retention — visit-by-visit detail rows
  retentionConversionFromLast: {
    getLabel: t =>
      t('page.guestJourney.retention.detailTable.rows.conversionFromLast'),
    getDescription: t =>
      t('page.guestJourney.definitions.retentionConversionFromLast'),
  },
  retentionMedianDays: {
    getLabel: t => t('page.guestJourney.retention.detailTable.rows.medianDays'),
    getDescription: t => t('page.guestJourney.definitions.retentionMedianDays'),
  },
};
